import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { api, type ProspectRunOption } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { formatDate, formatNumber } from "../../utils/format.ts";
import type { FilterOption, OptionsSpec } from "../filters/specs.ts";

/** The `?run=` param: one agent run's companies. */
export const RUN_PARAM = "run";

const ACTIVE = new Set(["pending", "queued", "running"]);

function runOption(run: ProspectRunOption): FilterOption {
  const what = run.subject ?? run.agent_name ?? "Agent run";
  return {
    value: run.id,
    label: `${what} · ${formatDate(run.created_at, "datetime")}`,
    hint: ACTIVE.has(run.status) ? "running" : formatNumber(run.prospect_count),
  };
}

/**
 * The "Agent run" filter: the tenant's prospecting runs newest first, searched
 * on the server by order title or agent name; with `collectionId`, only runs
 * that delivered into that collection. The picked run (often from a link) is
 * looked up by id so its pill names it.
 */
export function useAgentRunFilter(tenantId: string | null, runId: string, collectionId?: string): OptionsSpec {
  const { data: picked = [] } = useQuery({
    queryKey: keys.prospect.runOptions(tenantId, [runId]),
    queryFn: () => api.getProspectRunOptions({ tenant_id: tenantId!, ids: [runId] }),
    enabled: !!tenantId && !!runId,
    staleTime: 5 * 60_000,
  });
  const loadOptions = useCallback(
    async (q: string) =>
      tenantId
        ? (await api.getProspectRunOptions({ tenant_id: tenantId, collection_id: collectionId, q: q || undefined })).map(
            runOption,
          )
        : [],
    [tenantId, collectionId],
  );
  return useMemo<OptionsSpec>(
    () => ({
      kind: "options",
      key: "run",
      label: "Agent run",
      param: RUN_PARAM,
      group: "Found by",
      multiple: false,
      noneLabel: "Any run",
      loadOptions,
      // Only names the picked run on its pill; the list comes from `loadOptions`.
      options: picked.map(runOption),
      searchPlaceholder: "Search runs by order or agent",
    }),
    [loadOptions, picked],
  );
}
